import type { ReactNode } from 'react';
import styles from './SectionCard.module.css';
import { SearchBar } from '../../Search';
import SortByAlphaOutlined from '@mui/icons-material/SortByAlphaOutlined';
import { ListPageTableSkeleton } from '../../Skeletons/ListPageSkeleton';

export interface SectionTab<K extends string = string> {
	key: K;
	label: string;
	content: ReactNode;
}

export interface TabBarProps<K extends string = string> {
	tabs: Pick<SectionTab<K>, 'key' | 'label'>[];
	activeTab: K;
	onTabChange: (key: K) => void;
}

export const TabBar = <K extends string = string>({ tabs, activeTab, onTabChange }: TabBarProps<K>) => (
	<div className={styles.tabsWrapper}>
		{tabs.map((tab) => (
			<button
				key={tab.key}
				className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ''}`}
				onClick={() => onTabChange(tab.key)}
			>
				{tab.label}
			</button>
		))}
	</div>
);

export interface SectionCardProps<K extends string = string> {
	title: string;
	subtitle: string;
	tabs: SectionTab<K>[];
	activeTab: K;
	onTabChange: (key: K) => void;
	search?: boolean;
	searchQuery?: string;
	onSearch?: (q: string) => void;
	searchPlaceholder?: string;
	headerAction?: ReactNode;
	footer?: ReactNode;
	filterSlot?: ReactNode;
	sortAsc?: boolean;
	onToggleSort?: () => void;
	isLoading?: boolean;
	isFetching?: boolean;
	loadingLabel?: string;
}

export const SectionCard = <K extends string = string>({
	title,
	subtitle,
	tabs,
	activeTab,
	onTabChange,
	headerAction,
	footer,
	filterSlot,
	search,
	searchQuery = '',
	onSearch,
	searchPlaceholder,
	sortAsc,
	onToggleSort,
	isLoading = false,
	isFetching = false,
	loadingLabel,
}: SectionCardProps<K>) => {
	const activeContent = tabs.find((t) => t.key === activeTab)?.content;
	const activeLabel = tabs.find((t) => t.key === activeTab)?.label.toLowerCase() ?? '';
	return (
		<div className={styles.card}>
			<div className={styles.header}>
				<div className={styles.headerLeft}>
					<h2 className={styles.title}>{title}</h2>
					<span className={styles.subtitle}>{subtitle}</span>
				</div>
				{headerAction && <div className={styles.headerAction}>{headerAction}</div>}
			</div>

			<TabBar tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} />

			{search && (
				<div className={styles.filtersWrapper}>
					<div className={styles.searchWrapper}>
						{' '}
						<SearchBar
							value={searchQuery}
							placeholder={searchPlaceholder ?? `Buscar por ${activeLabel}...`}
							onChange={onSearch || (() => {})}
						/>
					</div>
					{onToggleSort && (
						<button
							type="button"
							className={`${styles.sortBtn} ${sortAsc ? styles.sortBtnActive : ''}`}
							onClick={onToggleSort}
							title="Ordenar em ordem alfabética"
						>
							<SortByAlphaOutlined />
						</button>
					)}
					{filterSlot}
				</div>
			)}

			<div className={styles.tabContent} aria-busy={isLoading || isFetching}>
				{isFetching && !isLoading && (
					<span role="status" className={styles.refreshStatus}>
						Atualizando dados…
					</span>
				)}
				{isLoading ? <ListPageTableSkeleton label={loadingLabel} /> : activeContent}
			</div>

			{footer}
		</div>
	);
};
